<template>
	<div class="bg-accented w-full pb-4 lg:pb-8">
		<div class="pt-8 px-4 lg:pt-12 h-full">
			<h3 class="lg:pb-8 pb-4 w-full flex flex-col space-y-2 text-center text-4xl lg:text-5xl">
				<span class="text-primary font-semibold">
					{{ $t('help-center.title1') }}
				</span>
				<span class="text-highlighted font-semibold">
					{{ $t('help-center.title2') }}
				</span>
			</h3>
			<div class="flex flex-col md:flex-row w-full md:items-stretch h-full justify-center gap-8 md:gap-6 my-8 items-center">
				<HelpCenterCard
					v-for="card in $tm('help-center.cards')"
					:key="card"
					:card="card"
					:tracking-location="CONTACT_PAGE"
				/>
			</div>
			<UButton
				:label="$t('help-center.contact-us')"
				size="xl"
				class="w-full max-w-[300px] flex justify-center text-center my-12 mx-auto"
				@click="$intercom.show()"
			/>

			<div class="max-w-3xl mx-auto">
				<PageDivider />
				<div class="px-6 py-8 lg:py-12">
					<div class=" font-semibold text-lg text-highlighted">
						{{ $t('help-center.call-us') }}
					</div>
					<p class="text-sm py-3">
						{{ $t('help-center.call-us-text') }}
					</p>
					<a
						:href="`tel:${CONTACT_PHONE_NUMBER}`"
						class="text-primary text-sm"
					>
						{{ $t('help-center.call-us') }}: {{ formatPhoneForDisplay(CONTACT_PHONE_NUMBER) }}
					</a>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { CONTACT_PAGE } from '@/constants/ga4/tracking-locations'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE,
	CONTACT_PHONE_NUMBER
} from '@/constants/index.js'
import {
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_REVIEW,
	JSON_LD_ORGANIZATION_SAME_AS
} from '@/constants/json-ld'
import { OG_IMAGE_DEFAULT } from '@/constants/og-image'
import { formatPhoneForDisplay } from '@/utils/formatPhone'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n({
	useScope: 'global'
})
const { getStory } = useStoryblokWithErrorReporting()
const route = useRoute()

const storyblok = await getStory('contact')
const { story } = storyblok.value?.data || {}
const {
	title, description
} = story?.content?.metatags || {
	title: t('help-center.meta-title'),
	description: t('help-center.meta-description')
}

useHead({
	title: t('help-center.meta-title'),
	meta: [
		{
			name: 'description',
			content: t('help-center.meta-description', { COMPANY_NAME })
		},
		{
			name: 'url',
			content: `${BASE_URL}/contact`
		}
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})
 import.meta.prerender && defineOgImage('Default', OG_IMAGE_DEFAULT)

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Contact',
					'item': `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Organization',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'url': BASE_URL,
			'telephone': COMPANY_PHONE,
			'description': t('meta-description', { COMPANY_NAME }),
			'contactPoint': JSON_LD_ORGANIZATION_CONTACT_POINT,
			'sameAs': JSON_LD_ORGANIZATION_SAME_AS,
			'address': JSON_LD_ORGANIZATION_ADDRESS,
			'review': JSON_LD_ORGANIZATION_REVIEW,
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		}
	]
})
</script>
